import { getListPickerOptions } from '../ui/interactiveMessageUtils';
import {
  ITEM_CONTENT_TYPES,
  QUEUE_TRIGGER_REGEX,
  AUTO_RESPONSES,
  INCOMING_TRIGGERS,
  CONFIRMATION_TRIGGER_REGEX
} from './constants';

type ChatTranscriptItem = connect.ChatTranscriptItem;
type ChatTypingEventData = connect.ChatTypingEvent['data'];
type ItemContentType = ChatTranscriptItem['ContentType'];
export type ItemTypeChecker = (item: ChatTranscriptItem | ChatTypingEventData) => boolean;
export type AttachmentChecker = (item: connect.AttachmentItem) => boolean;
export type IndexedItemTypeChecker = (
  item: ChatTranscriptItem | ChatTypingEventData,
  index: number,
  transcript: ChatTranscriptItem[]
) => boolean;
export type AnyItemTypeChecker = ItemTypeChecker | IndexedItemTypeChecker;

export const isListPickerSelection: IndexedItemTypeChecker = (item, index, transcript) => {
  // Which item was 'selected' for a ListPicker is determined by what the user sent as a message after a ListPicker was presented.
  const prevItem = transcript[index - 1];
  const prevItemOptions = getListPickerOptions(prevItem)?.map(i => i.title);
  const matchesPrevItemOption = item.Content && prevItemOptions?.includes(item.Content);
  const isSelection = isOutgoingMessage(item) && matchesPrevItemOption;

  return !!isSelection;
};

export const isIncoming: ItemTypeChecker = item => {
  return item.ParticipantRole !== 'CUSTOMER';
};

export const shouldSendReceipt: ItemTypeChecker = item => {
  return isMessage(item) && isAgentItem(item);
};

export const isMessage: ItemTypeChecker = item => {
  return item.Type === 'MESSAGE';
};

export const isAttachment: ItemTypeChecker = item => {
  return item.Type === 'ATTACHMENT';
};

export const isMarkdown: ItemTypeChecker = item => {
  return item.ContentType === 'text/markdown';
};

export const isInteractive: ItemTypeChecker = item => {
  return item.ContentType === ITEM_CONTENT_TYPES.INTERACTIVE;
};

export const isAgentItem: ItemTypeChecker = item => {
  return item.ParticipantRole === 'AGENT';
};

export const isCustomerItem: ItemTypeChecker = item => {
  return item.ParticipantRole === 'CUSTOMER';
};

export const isParticipantMessage: ItemTypeChecker = item => {
  return isMessage(item) && !isInteractive(item);
};

export const isIncomingMessage: ItemTypeChecker = item => {
  return isMessage(item) && isIncoming(item);
};

export const isIncomingAttachment: ItemTypeChecker = item => {
  return isAttachment(item) && isIncoming(item);
};

export const isOutgoingAttachment: ItemTypeChecker = item => {
  return isAttachment(item) && !isIncoming(item);
};

export const isOutgoingMessage: ItemTypeChecker = item => {
  return isMessage(item) && !isIncoming(item);
};

export const isInteractiveMessage: ItemTypeChecker = item => {
  return isMessage(item) && isInteractive(item);
};

export const isIncomingTriggerMessage: ItemTypeChecker = item => {
  return isIncoming(item) && INCOMING_TRIGGERS.includes(item.Content!);
};

export const isOutgoingTriggerMessage: ItemTypeChecker = item => {
  return !isIncoming(item) && AUTO_RESPONSES.includes(item.Content!);
};

export const isTriggerMessage: ItemTypeChecker = item => {
  return isIncomingTriggerMessage(item) || isOutgoingTriggerMessage(item);
};

export const isQueueTriggerMessage: ItemTypeChecker = item => {
  return !!(isIncoming(item) && item.Content && QUEUE_TRIGGER_REGEX.test(item.Content));
};

export const isWaitingForConfirmation: ItemTypeChecker = item => {
  return !!(isIncoming(item) && item.Content && CONFIRMATION_TRIGGER_REGEX.test(item.Content));
};

export const isAttachmentApproved: AttachmentChecker = attachment => {
  return attachment.Status === 'APPROVED';
};

export const isAttachmentRejected: AttachmentChecker = attachment => {
  return attachment.Status === 'REJECTED';
};

const RENDERED_EVENT_TYPES: ItemContentType[] = [
  ITEM_CONTENT_TYPES.JOINED,
  ITEM_CONTENT_TYPES.LEFT,
  ITEM_CONTENT_TYPES.ENDED
];

export const isEvent: ItemTypeChecker = item => {
  return item.Type === 'EVENT' && RENDERED_EVENT_TYPES.includes(item.ContentType);
};

export const isJoinedEvent: ItemTypeChecker = item => {
  return item.Type === 'EVENT' && item.ContentType === ITEM_CONTENT_TYPES.JOINED;
};

export const isLeftEvent: ItemTypeChecker = item => {
  return item.Type === 'EVENT' && item.ContentType === ITEM_CONTENT_TYPES.LEFT;
};

export const isAgentJoinedEvent: ItemTypeChecker = item => {
  return isAgentItem(item) && isJoinedEvent(item);
};

export const isAgentLeftEvent: ItemTypeChecker = item => {
  return isAgentItem(item) && isLeftEvent(item);
};

export const isGroupable = (itemA?: ChatTranscriptItem, itemB?: ChatTranscriptItem): boolean => {
  if (!itemA || !itemB) {
    return false;
  }
  const sameContentType = itemA.ContentType === itemB.ContentType;
  const sameParticipant = itemA.ParticipantId === itemB.ParticipantId;
  return sameContentType && sameParticipant;
};
